
import React from 'react';
import { Button } from '../../../components/base/Button';

export const ServicesSection: React.FC = () => {
  const services = [
    {
      icon: 'ri-pie-chart-line',
      title: 'Financial Planning & Analysis',
      description: 'Comprehensive financial planning services including budgeting, cash flow analysis, and long-term financial strategy development to secure your financial future.',
      features: ['Personal Budget Planning', 'Cash Flow Analysis', 'Financial Goal Setting', 'Risk Assessment'],
      image: 'https://public.readdy.ai/ai/img_res/c4ac8a75073f8b118e7b08fc9c04022f.jpg'
    },
    {
      icon: 'ri-stock-line',
      title: 'Investment Advisory Services',
      description: 'Expert investment guidance and portfolio management to help you build wealth through strategic investment decisions and market analysis.',
      features: ['Portfolio Management', 'Market Analysis', 'Investment Strategy', 'Risk Management'],
      image: 'https://readdy.ai/api/search-image?query=Investment%20portfolio%20analysis%20on%20computer%20screens%20showing%20stock%20charts%20and%20market%20data%2C%20professional%20trading%20desk%20environment%2C%20modern%20financial%20technology%20setup%20with%20multiple%20monitors%20displaying%20investment%20data&width=600&height=400&seq=service-002&orientation=landscape'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Business Development Consulting',
      description: 'Strategic business consulting to accelerate growth, optimize operations, and develop sustainable business models for long-term success.',
      features: ['Growth Strategy', 'Market Expansion', 'Operational Efficiency', 'Strategic Planning'],
      image: 'https://readdy.ai/api/search-image?query=Business%20development%20meeting%20with%20executives%20discussing%20growth%20strategies%2C%20modern%20conference%20room%20with%20presentation%20screens%20showing%20business%20charts%2C%20professional%20corporate%20consulting%20environment&width=600&height=400&seq=service-003&orientation=landscape'
    },
    {
      icon: 'ri-user-star-line',
      title: 'Career Development Coaching',
      description: 'Personalized career coaching and professional development services to help you advance your career and achieve your professional goals.',
      features: ['Career Planning', 'Leadership Development', 'Skill Assessment', 'Executive Coaching'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20career%20coaching%20session%20with%20mentor%20and%20client%20discussing%20career%20development%20plans%2C%20modern%20office%20environment%20with%20career%20planning%20materials%20and%20documents%2C%20inspiring%20professional%20atmosphere&width=600&height=400&seq=service-004&orientation=landscape'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Retirement Planning',
      description: 'Comprehensive retirement planning services to ensure you have the financial security and peace of mind for your golden years.',
      features: ['Retirement Savings', '401(k) Optimization', 'Social Security Planning', 'Estate Planning'],
      image: 'https://readdy.ai/api/search-image?query=Retirement%20planning%20consultation%20with%20financial%20documents%20and%20retirement%20savings%20charts%2C%20peaceful%20office%20setting%20with%20older%20adults%20discussing%20financial%20security%2C%20professional%20advisory%20atmosphere&width=600&height=400&seq=service-005&orientation=landscape'
    },
    {
      icon: 'ri-calculator-line',
      title: 'Tax Strategy & Optimization',
      description: 'Strategic tax planning and optimization services to minimize your tax burden while maximizing your financial efficiency and compliance.',
      features: ['Tax Planning', 'Deduction Optimization', 'Tax Compliance', 'Strategic Structuring'],
      image: 'https://readdy.ai/api/search-image?query=Tax%20planning%20and%20strategy%20session%20with%20financial%20advisor%20reviewing%20tax%20documents%20and%20calculations%2C%20organized%20office%20environment%20with%20tax%20forms%20and%20financial%20statements%2C%20professional%20tax%20consultation%20setting&width=600&height=400&seq=service-006&orientation=landscape'
    }
  ];

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
            Our <span className="text-blue-600">Expert Services</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive financial advisory and business development services tailored to your unique needs and goals. 
            Our expert team provides personalized solutions to help you achieve sustainable growth and financial success.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {services.map((service, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="md:flex">
                <div className="md:w-1/2">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-64 md:h-full object-cover object-top"
                  />
                </div>
                <div className="md:w-1/2 p-8">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <i className={`${service.icon} text-2xl text-blue-600`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                  </div>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-700">
                        <i className="ri-check-line text-teal-500 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                  >
                    Learn More
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <Button 
            variant="primary" 
            size="lg"
            onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
          >
            <i className="ri-calendar-line mr-2"></i>
            Schedule Your Free Consultation
          </Button>
        </div>
      </div>
    </section>
  );
};
